/**
 * Configuration par tenant — la cadence de relance, pour commencer.
 *
 * `tenant_config` existe en base depuis la phase 1 et n'était lue nulle part :
 * une table créée pour éviter les `if (tenant === 'X')` dans le code, et que
 * le code ignorait. Ce domaine est ce qui la rend vivante.
 *
 * Les seuils de relance vivaient jusqu'ici en deux exemplaires — dans
 * `InvoiceDetail.tsx` pour la relance manuelle, dans la fonction planifiée
 * pour la relance automatique — sans rien pour les tenir ensemble. Deux
 * copies de la même règle divergent ; c'est une question de temps, pas de
 * discipline.
 */

/**
 * Les quatre paliers de relance, en jours autour de l'échéance.
 *
 * Le ton monte avec le retard et il monte tout seul : le patron ne choisit
 * pas un « niveau » dans une liste, il règle une fois la cadence de son
 * entreprise. Le niveau 4 est une mise en demeure — elle produit des effets
 * juridiques, d'où un palier explicite plutôt qu'un « et après ».
 */
export interface ReminderCadence {
  /**
   * Jours **avant** échéance pour le rappel préventif (niveau 1).
   *
   * Ce palier n'est pas déclenché automatiquement aujourd'hui : il sert au
   * choix du ton d'une relance manuelle tirée avant l'échéance. Le passer au
   * cron reviendrait à ouvrir un envoi sortant qui n'existe pas encore.
   */
  readonly preventiveDaysBefore: number;
  /** Jours **après** échéance — relance amicale (niveau 2). */
  readonly friendlyDaysAfter: number;
  /** Jours **après** échéance — relance ferme (niveau 3). */
  readonly firmDaysAfter: number;
  /** Jours **après** échéance — mise en demeure (niveau 4). */
  readonly formalDaysAfter: number;
}

export type ReminderLevel = 1 | 2 | 3 | 4;

/**
 * La cadence en vigueur avant que le réglage n'existe.
 *
 * Volontairement identique au comportement d'hier, et non au J+30/45/60 du
 * plan : rendre le réglage configurable ne doit pas changer en silence le ton
 * des relances déjà en cours chez les entreprises qui n'ont rien demandé.
 * Celle qui veut J+30/45/60 la pose désormais elle-même.
 */
export const DEFAULT_REMINDER_CADENCE: ReminderCadence = {
  preventiveDaysBefore: 3,
  friendlyDaysAfter: 1,
  firmDaysAfter: 15,
  formalDaysAfter: 30,
};

/**
 * Le ton qui convient **maintenant** pour une facture en retard de `daysLate`
 * jours. C'est la question de l'écran : le patron ouvre une facture et tire
 * une relance.
 *
 * Une facture pas encore échue ne reçoit jamais mieux qu'un rappel, quelle
 * que soit la cadence : on ne met pas en demeure pour une dette qui n'est pas
 * encore due.
 */
export function reminderLevelFor(
  daysLate: number,
  cadence: ReminderCadence = DEFAULT_REMINDER_CADENCE,
): ReminderLevel {
  if (daysLate < 0) return 1;
  if (daysLate < cadence.firmDaysAfter) return 2;
  if (daysLate < cadence.formalDaysAfter) return 3;
  return 4;
}

/**
 * Le palier que la facture vient de **franchir**, ou `null`. C'est l'autre
 * question, celle du cron : qui relancer cette nuit.
 *
 * Franchir, et non « avoir dépassé ». Une facture oubliée depuis six mois a
 * dépassé les trois paliers ; si le cron répondait par le dernier, un
 * raccordement tardif enverrait une salve de mises en demeure à tout
 * l'encours ancien. Le palier ne vaut que le jour où il tombe, plus quelques
 * jours de battement pour absorber une nuit où le cron n'a pas tourné.
 */
export function reminderDueAt(
  daysLate: number,
  cadence: ReminderCadence = DEFAULT_REMINDER_CADENCE,
  graceDays = 2,
): ReminderLevel | null {
  const crossed = (threshold: number) =>
    daysLate >= threshold && daysLate < threshold + graceDays;

  // Du plus grave au plus léger : quand deux paliers se chevauchent parce que
  // la cadence est serrée, c'est le plus grave qui l'emporte. L'inverse
  // enverrait une relance amicale à qui mérite une mise en demeure.
  if (crossed(cadence.formalDaysAfter)) return 4;
  if (crossed(cadence.firmDaysAfter)) return 3;
  if (crossed(cadence.friendlyDaysAfter)) return 2;
  return null;
}

/**
 * Identité visuelle du tenant.
 *
 * Séparée des autres réglages parce qu'elle est la seule partie lisible
 * **sans authentification** : l'écran de connexion doit s'afficher aux
 * couleurs du client alors que personne n'est encore connecté. C'est
 * `branding_for_host` qui la sert alors, et elle ne renvoie que ces champs
 * d'affichage — jamais le `tenant_id`, qu'un appelant serait tenté d'utiliser
 * pour autoriser.
 */
export interface TenantBranding {
  /**
   * URL absolue du logo, ou `null`. Une URL et non un chemin de stockage :
   * l'entreprise qui héberge déjà son logo ailleurs n'a pas à le redéposer,
   * et `branding_for_host` la sert telle quelle à un visiteur anonyme.
   */
  readonly logoUrl: string | null;
  /** Couleur d'accent, en hexadécimal `#RRGGBB`. */
  readonly primaryColor: string;
}

/**
 * L'accent par défaut — l'ambre de l'interface.
 *
 * Une valeur et non `null` : tout le reste de l'interface s'en sert pour
 * dériver un survol et une couleur de texte lisible, et ces calculs n'ont pas
 * à traiter le cas « pas de couleur ».
 */
export const DEFAULT_PRIMARY_COLOR = "#E8A838";

export const DEFAULT_BRANDING: TenantBranding = {
  logoUrl: null,
  primaryColor: DEFAULT_PRIMARY_COLOR,
};

/**
 * L'objectif de marge que l'entreprise se fixe, en points.
 *
 * `null` est un état et non un trou : « je ne me fixe pas d'objectif ». Le
 * tableau de bord retombe alors sur la comparaison au mois précédent, qui est
 * ce qu'il affichait avant que ce réglage n'existe. Traiter `null` comme un
 * zéro afficherait « objectif 0 % — atteint » à toute entreprise qui n'a rien
 * renseigné, ce qui est pire que de ne rien dire.
 *
 * Une visée d'ensemble, jamais un objectif par chantier : la fiche chantier
 * compare déjà sa marge constatée à celle de son devis, et c'est la bonne
 * référence à cette échelle. Un chantier devisé à 12 % n'a pas à être jugé sur
 * l'objectif d'entreprise.
 */
export type MarginTarget = number | null;

/**
 * Aucun objectif tant que le patron n'en a pas posé un.
 *
 * Contrairement à la cadence de relance, il n'y a pas de valeur « raisonnable »
 * à inventer ici. 20 % conviendrait à un plaquiste et ruinerait un terrassier ;
 * un défaut chiffré serait une opinion déguisée en réglage d'usine.
 */
export const DEFAULT_MARGIN_TARGET: MarginTarget = null;

/**
 * Les taux de majoration, en pourcentage.
 *
 * Les **tranches** sont celles du Code du travail et ne se règlent pas : du
 * contrat à 35 h (heures complémentaires d'un temps partiel), du contrat ou de
 * 35 h jusqu'à 43 h, au-delà de 43 h. Les **taux** se règlent, parce qu'un
 * accord d'entreprise peut les changer. Le calcul vit en base
 * (`recalc_overtime_week`) : c'est lui qui fait le coût des chantiers.
 */
export interface OvertimeRates {
  /** Heures complémentaires d'un temps partiel, jusqu'à 35 h. */
  readonly complementaryRate: number;
  /** De 35 h (ou du contrat) à 43 h. */
  readonly firstRate: number;
  /** Au-delà de 43 h. */
  readonly secondRate: number;
}

/**
 * Les taux légaux à défaut d'accord.
 *
 * Contrairement à l'objectif de marge, un défaut chiffré n'est pas ici une
 * opinion : c'est la loi (L3121-36 pour les heures supplémentaires, L3123-8
 * pour les complémentaires). Une entreprise sous accord les ajuste.
 */
export const DEFAULT_OVERTIME_RATES: OvertimeRates = {
  complementaryRate: 10,
  firstRate: 25,
  secondRate: 50,
};

/**
 * Les réglages de sécurité.
 *
 * La fréquence des quarts d'heure sécurité, en jours. Aucun texte ne la fixe :
 * hebdomadaire par défaut, parce que c'est la cadence usuelle sur chantier ;
 * une PME qui en tient un par mois la règle, et n'est plus relancée chaque
 * semaine.
 */
export interface SafetySettings {
  readonly talkFrequencyDays: number;
}

export const DEFAULT_SAFETY_SETTINGS: SafetySettings = { talkFrequencyDays: 7 };

/** La fréquence lue depuis `tenant_config.settings.safety`, bornée à 1–90 jours. */
export function safetySettingsFrom(raw: unknown): SafetySettings {
  const bloc = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const v = bloc.talk_frequency_days;
  const n = typeof v === "string" ? Number(v) : v;
  return {
    talkFrequencyDays:
      typeof n === "number" && Number.isInteger(n) && n >= 1 && n <= 90
        ? n
        : DEFAULT_SAFETY_SETTINGS.talkFrequencyDays,
  };
}

/**
 * Les réglages de la sous-traitance.
 *
 * Le Code du travail n'impose l'obligation de vigilance (attestation URSSAF,
 * Kbis) qu'au-delà de 5 000 € HT. Par défaut, DKER réclame ces pièces pour
 * tout marché : c'est le plus sûr. Le patron qui veut s'en tenir au seuil
 * l'active ici. Les assurances restent réclamées dans tous les cas — le seuil
 * ne les concerne pas.
 *
 * Ce qui se compare au seuil, le patron le choisit aussi : le **cumul** des
 * marchés en cours avec un même sous-traitant (par défaut, le plus prudent :
 * découper un marché ne fait pas taire la vigilance), ou chaque **marché**
 * pris seul.
 */
export type VigilanceBasis = "cumulative" | "per_contract";

export const VIGILANCE_BASES: readonly VigilanceBasis[] = ["cumulative", "per_contract"];

export interface SubcontractingSettings {
  readonly applyVigilanceThreshold: boolean;
  readonly vigilanceBasis: VigilanceBasis;
}

export const DEFAULT_SUBCONTRACTING_SETTINGS: SubcontractingSettings = {
  applyVigilanceThreshold: false,
  vigilanceBasis: "cumulative",
};

/** Le réglage lu depuis `tenant_config.settings.subcontracting`. */
export function subcontractingSettingsFrom(raw: unknown): SubcontractingSettings {
  const bloc = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return {
    applyVigilanceThreshold: bloc.apply_vigilance_threshold === true,
    vigilanceBasis: bloc.vigilance_basis === "per_contract" ? "per_contract" : "cumulative",
  };
}

/** Configuration complète d'un tenant, telle que l'application la lit. */
export interface TenantConfig {
  readonly reminders: ReminderCadence;
  readonly branding: TenantBranding;
  readonly marginTarget: MarginTarget;
  readonly overtime: OvertimeRates;
  readonly safety: SafetySettings;
  readonly subcontracting: SubcontractingSettings;
}

/** Où se situe une marge constatée par rapport à l'objectif. */
export type MarginStanding = "atteint" | "proche" | "sous";

/** En dessous de combien de points d'écart on parle encore d'« à portée ». */
const MARGE_DE_MANOEUVRE = 2;

/**
 * Le verdict de la jauge.
 *
 * Trois états et non deux : un objectif manqué d'un demi-point n'est pas un
 * objectif manqué de dix, et peindre les deux en rouge fait qu'on cesse de
 * regarder la couleur. `null` quand l'un des deux chiffres manque — on ne juge
 * pas une marge inconnue.
 */
export function marginStanding(
  actual: number | null,
  target: MarginTarget,
): MarginStanding | null {
  if (actual == null || target == null) return null;
  if (actual >= target) return "atteint";
  return actual >= target - MARGE_DE_MANOEUVRE ? "proche" : "sous";
}

/**
 * Les taux lus depuis `tenant_config.settings.overtime`.
 *
 * Même prudence que pour la cadence : le JSON est libre, et un taux absent ou
 * illisible retombe sur le taux légal plutôt que sur zéro — une majoration à 0
 * par accident ferait paraître rentable un chantier qui ne l'est pas, soit le
 * défaut que ce réglage existe pour corriger.
 */
export function overtimeRatesFrom(raw: unknown): OvertimeRates {
  const bloc = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const lire = (cle: string, defaut: number) => {
    const v = bloc[cle];
    const n = typeof v === "string" ? Number(v) : v;
    return typeof n === "number" && Number.isFinite(n) && n >= 0 && n <= 200 ? n : defaut;
  };
  return {
    complementaryRate: lire("complementary_rate", DEFAULT_OVERTIME_RATES.complementaryRate),
    firstRate: lire("first_rate", DEFAULT_OVERTIME_RATES.firstRate),
    secondRate: lire("second_rate", DEFAULT_OVERTIME_RATES.secondRate),
  };
}
